import * as moment from "moment";
import { User } from "reauthorize";

import { AnsweredQuestion, AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";

import { TaskModel } from "./task/taskActions/models";
import { Profession } from "./adminUser/models";

export interface QuestionProps {
    id: string;
    question: string;
    answer?: string;
    updateQuestion?: (question: AnsweredQuestion) => void;
}

export type QuestionMap = { [id: string]: AnsweredQuestion };

export interface LinkObject {
    id: string;
    name: string;
}

export interface ClinicianLink {
    id: string;
    name: string;
    qualification: string;
    profession?: Profession;
}

export interface CurrentUser extends User {
    id?: string;
    name: string;
    email?: string;
    unauthorizedDueToNoAssignedRoles?: boolean;
}

export enum CaseStatus {
    Open = "Open",
    Closed = "Closed",
    Cancelled = "Cancelled",
    Recall = "Recall" // Should only be applied to HS cases
}

export const CASE_STATUSES = [
    { displayName: CaseStatus.Open, value: CaseStatus.Open },
    { displayName: CaseStatus.Closed, value: CaseStatus.Closed },
    { displayName: CaseStatus.Cancelled, value: CaseStatus.Cancelled },
    { displayName: CaseStatus.Recall, value: CaseStatus.Recall }
];

export enum CaseType {
    REFERRAL = "Referral",
    PPQ = "PPQ",
    BBV = "Blood Borne Virus",
    IMMUNISATION = "Immunisation",
    HEALTH_SURVEILLANCE = "Health Surveillance",
    PHYSIOTHERAPY_REFERRAL = "Physiotherapy Referral",
    WORK_ADJUSTMENT = "Work Adjustment",
    GENERIC = "Generic",
    COVID_19 = "COVID-19",
    SRA = "SRA",
    MHP_TREATMENT = "MHP Treatment"
}

export const PRODUCT_TYPES = [
    { displayName: "Referrals", value: CaseType.REFERRAL },
    { displayName: "Pre-Placement Questionnaires", value: CaseType.PPQ },
    { displayName: "Blood Borne Virus", value: CaseType.BBV },
    { displayName: "Immunisations and Vaccinations", value: CaseType.IMMUNISATION },
    { displayName: "Health Surveillance", value: CaseType.HEALTH_SURVEILLANCE },
    { displayName: "Physiotherapy Referral", value: CaseType.PHYSIOTHERAPY_REFERRAL },
    { displayName: "Work Adjustment", value: CaseType.WORK_ADJUSTMENT },
    { displayName: "Generic", value: CaseType.GENERIC },
    { displayName: "COVID-19", value: CaseType.COVID_19 },
    { displayName: "Assessment and Well-being", value: CaseType.SRA },
    { displayName: "MHP Treatment", value: CaseType.MHP_TREATMENT }
];

export enum PhysiotherapyOriginType {
    REFERRAL = "Referral",
    SELF_REFERRAL = "Self Referral"
}

export const PHYSIOTHERAPY_ORIGINS = [
    { displayName: "Referral", value: PhysiotherapyOriginType.REFERRAL },
    { displayName: "Self Referral", value: PhysiotherapyOriginType.SELF_REFERRAL }
];

export const CUSTOMER_USER_CASE_TYPES = [
    { displayName: "Referral", value: CaseType.REFERRAL },
    { displayName: "Pre-Placement Questionnaire", value: CaseType.PPQ },
    { displayName: "Physiotherapy Referral", value: CaseType.PHYSIOTHERAPY_REFERRAL },
    { displayName: "Health Surveillance", value: CaseType.HEALTH_SURVEILLANCE }
] as const;

export const REFERRAL_FEATURES = [
    { displayName: "Work Adjustment", value: CaseType.WORK_ADJUSTMENT }
];

export enum Covid19FeatureType {
    PPQ_RISK_ASSESSMENT = "COVID-19 PPQ Risk Assessment"
}

export const COVID_19_FEATURES = [
    { displayName: "COVID-19 PPQ Risk Assessment", value: Covid19FeatureType.PPQ_RISK_ASSESSMENT }
];

export enum AssessmentAndWellBeingFeatureType {
    STRESS_RISK_ASSESSMENT = "Stress Risk Assessment"
}

export const ASSESSMENT_AND_WELL_BEING_FEATURES = [
    { displayName: "Stress Risk Assessment", value: AssessmentAndWellBeingFeatureType.STRESS_RISK_ASSESSMENT }
];

export { AnsweredQuestion, AnsweredQuestionWithId };

export interface EmployeeRecord {
    readonly id: string;
    readonly firstName?: string;
    readonly lastName?: string;
    readonly title?: string;
    readonly gender?: string | null;
    readonly dateOfBirth?: moment.Moment | null;
    readonly customerName?: string;
    readonly orgStructureOne?: LinkObject;
    readonly orgStructureTwo?: { name: string };
    readonly jobTitle?: string;
    readonly addressLine1?: string;
    readonly addressLine2?: string;
    readonly addressLine3?: string;
    readonly city?: string;
    readonly county?: string;
    readonly postCode?: string;
    readonly personalEmailAddress?: string;
    readonly personalReferenceNumber?: string;
    readonly secondaryReference?: string;
    readonly homePhoneNumber?: string;
    readonly mobilePhoneNumber?: string;
}

export interface ConsultationDetails {
    consultationTaskId: string;
    referralReportTaskId?: string;
    ohCaseId: string;
    lineManagersNames: string[];
    lineManagersEmails: string[];
    hrNames: string[];
    hrEmails: string[];
    consultationDate: moment.Moment | null;
    consultationType: string;
    // Employee unique id (from the questionnaire)
    employeeId: string;
    secondaryReferenceNumber: string;
    informationRequiredSection?: AnsweredQuestionWithId[];
    occupationalHealthAdviserName?: string;
    occupationalHealthAdviserTitle?: string;
    occupationalHealthAdviserQualification?: string;
    reasonForReferral?: string;
    selfManagementWithAdviceAndExercises?: string;
    numberOfSessionsIncludingInitialAssessment?: string;
    customerEmail?: string;
    customerPhone?: string;
}

export interface OhCase {
    readonly id: string;
    readonly caseId?: string;
    readonly customer?: LinkObject;
    readonly employeeRecordId?: string;
    readonly employeeFirstName?: string;
    readonly employeeLastName?: string;
    readonly tasks?: TaskModel[];
}

export enum OhdPaymentMethodsType {
    PaidByCard = "Paid By Card",
    PayByInvoice = "Pay By Invoice"
}

export const OhdPaymentMethodOptions = [
    OhdPaymentMethodsType.PaidByCard,
    OhdPaymentMethodsType.PayByInvoice
];

export const isYesNoOptionEnabledOptions: DropdownOption[] = [
    { text: "Yes", value: String(true) },
    { text: "No", value: String(false) }
];

export const getYesNoOptionEnabledDisplayValue = (value: boolean | undefined): string => {
    return isYesNoOptionEnabledOptions.find(o => o.value === JSON.stringify(value))?.text ?? "";
};
